// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  AccessTime as PendingIcon,
  Warning as WarningIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';

interface ContractEvent {
  event_type: string;
  role: string;
  status: string;
  timestamp: string;
  documentUrl?: string;
  documentUrls?: string[];
}

interface Contract {
  productFamily: string;
  events: ContractEvent[];
  lastUpdated: string;
  documentUrl?: string;
}

interface DealContractsProps {
  deal: any;
}

const DealContracts: React.FC<DealContractsProps> = ({ deal }) => {
  const { theme } = useTheme();

  const contracts = useMemo(() => {
    if (!deal?.flexAttributes) {
      return [];
    }

    try {
      const flexAttrs =
        typeof deal.flexAttributes === 'string'
          ? JSON.parse(deal.flexAttributes)
          : deal.flexAttributes;

      const docusealData = flexAttrs?.docuseal;
      if (!docusealData) {
        return [];
      }

      const contractsArray: Contract[] = [];

      Object.keys(docusealData).forEach((productFamily) => {
        const contractData = docusealData[productFamily];
        let events: ContractEvent[] = [];

        if (contractData.history && Array.isArray(contractData.history)) {
          events = contractData.history.map((historyItem: any) => ({
            event_type: historyItem.event_type,
            role: historyItem.role,
            status: historyItem.status,
            timestamp: historyItem.timestamp,
            documentUrl: historyItem.documentUrl,
            documentUrls: historyItem.documentUrls,
          }));
        } else if (
          contractData.role &&
          contractData.status &&
          contractData.timestamp
        ) {
          events.push({
            event_type: 'contract_status',
            role: contractData.role,
            status: contractData.status,
            timestamp: contractData.timestamp,
            documentUrl: contractData.documentUrl,
          });
        }

        events.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        if (events.length > 0) {
          contractsArray.push({
            productFamily,
            events,
            lastUpdated: contractData.timestamp,
            documentUrl: contractData.documentUrl,
          });
        }
      });

      return contractsArray.sort(
        (a, b) =>
          new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      );
    } catch (err) {
      console.error('Error parsing contract data:', err);
      return [];
    }
  }, [deal]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckIcon sx={{ fontSize: 16, color: '#4CAF50' }} />;
      case 'created':
        return <AddIcon sx={{ fontSize: 16, color: '#2196F3' }} />;
      case 'sent':
        return <EmailIcon sx={{ fontSize: 16, color: '#FF9800' }} />;
      case 'opened':
        return <ViewIcon sx={{ fontSize: 16, color: '#9C27B0' }} />;
      case 'pending':
        return <PendingIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />;
      default:
        return <WarningIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />;
    }
  };

  const formatContractName = (productFamily: string) => {
    return (
      productFamily.charAt(0).toUpperCase() +
      productFamily.slice(1) +
      ' Contract'
    );
  };

  const formatEventDescription = (event: ContractEvent) => {
    const capitalizedRole =
      event.role.charAt(0).toUpperCase() + event.role.slice(1);

    if (event.event_type) {
      switch (event.event_type) {
        case 'form.completed':
          return `${capitalizedRole} Completed Contract`;
        case 'form.opened':
          return `${capitalizedRole} Viewed Contract`;
        case 'form.sent':
          return `Contract Sent to ${capitalizedRole}`;
        case 'form.created':
          return 'Contract Created';
        default:
          return `${capitalizedRole} ${
            event.event_type.replace('form.', '').charAt(0).toUpperCase() +
            event.event_type.replace('form.', '').slice(1)
          } Contract`;
      }
    }

    switch (event.status) {
      case 'completed':
        return `${capitalizedRole} Signed Contract`;
      case 'created':
        return 'Contract Created';
      case 'sent':
        return `Contract Sent to ${capitalizedRole}`;
      case 'opened':
        return `${capitalizedRole} Viewed Contract`;
      case 'pending':
        return `Awaiting ${capitalizedRole} Action`;
      default:
        return `${capitalizedRole} ${
          event.status.charAt(0).toUpperCase() + event.status.slice(1)
        } Contract`;
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDocumentClick = (event: ContractEvent) => {
    let urlToOpen = event.documentUrl;
    if (!urlToOpen && event.documentUrls && event.documentUrls.length > 0) {
      urlToOpen = event.documentUrls[0];
    }
    if (urlToOpen) {
      window.open(urlToOpen, '_blank');
    }
  };

  if (contracts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 5,
        }}
      >
        <DocIcon sx={{ fontSize: 48, color: theme.textLight, mb: 2 }} />
        <Typography sx={{ color: theme.textLight, fontSize: 14 }}>
          No contract history available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {contracts.map((contract, index) => {
        const contractKey = `${contract.productFamily}-${index}`;

        return (
          <Accordion
            key={contractKey}
            defaultExpanded={index === 0}
            sx={{
              backgroundColor: theme.background2,
              borderRadius: '8px !important',
              mb: 1.5,
              '&:before': { display: 'none' },
              boxShadow: 'none',
              border: `1px solid ${theme.stroke}`,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: theme.text }} />}
              sx={{ px: 2 }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <DocIcon sx={{ fontSize: 20, color: theme.primary }} />
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 700, color: theme.text }}
                  >
                    {formatContractName(contract.productFamily)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: theme.textLight }}>
                  Last updated: {formatDateTime(contract.lastUpdated)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0 }}>
              {contract.events.map((event, eventIndex) => (
                <Box
                  key={eventIndex}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    py: 1,
                    borderTop:
                      eventIndex > 0 ? `1px solid ${theme.stroke}` : 'none',
                  }}
                >
                  <Box sx={{ mt: 0.25 }}>{getStatusIcon(event.status)}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 600, color: theme.text }}
                    >
                      {formatEventDescription(event)}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: theme.textLight, mt: 0.25 }}
                    >
                      {formatDateTime(event.timestamp)}
                    </Typography>
                    {(event.documentUrl ||
                      (event.documentUrls &&
                        event.documentUrls.length > 0)) && (
                      <Link
                        component="button"
                        onClick={() => handleDocumentClick(event)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.5,
                          fontSize: 12,
                          color: theme.primary,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <OpenIcon sx={{ fontSize: 14 }} />
                        View document
                      </Link>
                    )}
                  </Box>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default DealContracts;
